import { getModel } from '../../model/registry'
import { useCallback, useRef, useState } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import useApi from '../../hooks/useApi'
import useTranslation from '../../hooks/useTranslation'
import useFormModal from '../modal/useModalDialog'
import pause from '../../utils/pause'
import invalidateListQueries from '../../utils/invalidateListQueries'
import { useMessage } from '../pageView/Page'
import { toGuardMessage } from '../../utils/guardMessage'

import runValidationLogic from '../../../../common/logic/runValidationLogic.js'

const useListActions = (config, endpoint, setModalType, onRefreshAggregate) => {
  const { setShowModal, setModalConfig } = useFormModal()

  const pendingActionRef = useRef(null)

  const { deleteMe, postMe, getMe, putMe } = useApi()
  const [, setMessage] = useMessage((s) => s)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Move-to-folder state
  const [moveItem, setMoveItem] = useState(null)
  const [moveLoading, setMoveLoading] = useState(false)

  const deleteItemFn = async (item) => {
    const response = await deleteMe(endpoint, item.id)
    const isError = response?.status === 'error' || response?.error
    if (!isError) {
      await invalidateListQueries(endpoint, queryClient)
    }
    await pause(250)
    return response
  }

  const postActionFn = async (action, item) => {
    const response = await postMe(`${endpoint}/${item.id}/${action}`, item)
    const isError = response?.status === 'error' || response?.error
    if (!isError) {
      await invalidateListQueries(endpoint, queryClient)
    }
    await pause(250)
    return response
  }

  const goTo = (target, item) => {
    const url = target.replace(':id', item.id)
    navigate(url)
  }

  const actionHandler = async (option, item) => {
    let response

    // Clear any bar left over from a previous attempt — MessageBar only self-closes
    // on 'success', so a stale warning would otherwise outlive the row it referred to.
    setMessage({ show: false })

    // Client-side pre-check. The same guards run again on the server, so this only
    // saves a round trip — the server response below is the authority.
    const result = await runValidationLogic(config.entity, option.action, item, getMe)
    if (!result.valid) {
      setMessage(toGuardMessage(result))
      return { msg: 'Validation Error' }
    }

    switch (option.action) {
      case 'delete':
        response = await deleteItemFn(item)
        break
      case 'edit':
        goTo(`:id`, item)
        break
      case 'addChild': {
        const { newPath, stateFields } = option
        const navState = stateFields
          ? Object.fromEntries(stateFields.map(({ to, from }) => [to, item[from]]))
          : {}
        navigate(newPath, { state: navState })
        break
      }
      case 'transact':
        goTo('/transactions/:id', item)
        break
      case 'activate':
        response = await postActionFn('activate', item)
        break
      case 'openUrl':
        if (item.url) window.open(item.url, '_blank', 'noopener,noreferrer')
        break
      default:
        response = await postActionFn(option.action, item)
        break
    }

    // A guard the client could not evaluate (or evaluated against stale data) is
    // refused by the server. Surface its reason rather than letting the row quietly
    // reappear on the next refetch.
    const guardMessage = toGuardMessage(response)
    if (guardMessage) setMessage(guardMessage)

    if (option?.refreshAggregate && onRefreshAggregate && response?.status !== 'error') {
      onRefreshAggregate()
    }

    return response
  }

  const actionMutation = useMutation({
    mutationFn: ({ option, item }) => actionHandler(option, item)
  })

  const runConfirm = async (option) => {
    setModalConfig(getModel('modalDialogData')[option.modalConfig])
    if (setModalType) setModalType('line')
    setShowModal(true)
  }

  const handleAction = useCallback(async (menuItem, item) => {
    // Move opens a folder-picker modal — no mutation needed
    if (menuItem.action === 'move') {
      setMoveItem(item)
      return
    }

    if (menuItem.confirm) {
      if (menuItem.modalConfig) {
        pendingActionRef.current = { menuItem, item }
        runConfirm(menuItem)
        return
      } else {
        const confirmed = window.confirm(t('all.confirm.delete.accept') || 'Are you sure?')
        if (!confirmed) return
      }
    }

    await actionMutation.mutateAsync({ option: menuItem, item })
  }, [actionMutation, t])

  // Called by MoveFolderModal when a folder is confirmed
  const confirmMove = useCallback(async (folder) => {
    if (!moveItem) return
    setMoveLoading(true)
    try {
      await putMe('document', {
        folderId: folder.id,
        folderName: folder.name,
        folderPath: folder.path,
      }, moveItem.id)
      await invalidateListQueries(endpoint, queryClient)
      setMoveItem(null)
    } finally {
      setMoveLoading(false)
    }
  }, [moveItem, endpoint, queryClient, putMe])

  const clearMoveItem = useCallback(() => setMoveItem(null), [])

  const handleModalAction = useCallback(async (actionData) => {
    const { action } = actionData
    setShowModal(false)

    if (action !== 'close' && pendingActionRef.current) {
      const { menuItem, item } = pendingActionRef.current
      const confirmedMenuItem = { ...menuItem, action }
      await actionMutation.mutateAsync({ option: confirmedMenuItem, item })
      pendingActionRef.current = null
    }
  }, [actionMutation, setShowModal])

  return {
    handleAction,
    handleActions: handleModalAction,
    isLoading: actionMutation.isPending,
    moveItem,
    moveLoading,
    confirmMove,
    clearMoveItem,
  }
}

export default useListActions
